import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import NextImage from "next/image"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import IconArrowRight from "@jobteaser/ui-kit/assets/icons/jds_arrowRight.svg"
import { Logo } from "@jobteaser/ui-kit/components/Image/Logo"
import { Link } from "@jobteaser/ui-kit/components/Link"
import { Icon, ICON_SIZES } from "@jobteaser/ui-kit/dist/module/components/Icon/Icon"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/dist/module/components/Layout/Layout"
import { RichText } from "@jobteaser/ui-kit/dist/module/components/RichText/RichText"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { INSIGHT_ROUTE } from "@/modules/community/constants/route"
import { useCompanyInsightsFetch } from "@/modules/community/hooks/useCompanyInsightsFetch"
import { EVENT_ROUTE } from "@/modules/companies/client/constants/routes"
import { useCompanyEventsFetch } from "@/modules/companies/client/hooks/useCompanyEventsFetch"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { COMPANY_BASENAME } from "@/modules/companies/client/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useLocale } from "@/modules/locales/useLocale"
import { Link as NextLink } from "@/modules/routing/components/Link"

import styles from "./Exclusivity.module.css"

export const Exclusivity: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCompany } = useCompanyFetch()
  const { dataJobAds } = useJobAdsFromAlgolia()
  const { dataCompanyInsights } = useCompanyInsightsFetch(dataCompany?.id || "")
  const { dataCompanyEvents } = useCompanyEventsFetch()
  const locale = useLocale()
  const { companySlug } = useParams()

  if (!dataCompany || !dataCareerCenter) return null

  const { name, customMessage } = dataCompany.page
  const { shortName, intranet, logo } = dataCareerCenter

  const companyPath = `/${locale}/${COMPANY_BASENAME}/${companySlug}`

  const countExclusiveJobAds = dataJobAds?.jobAds.filter(jobAd => jobAd.exclusive).length || 0
  const countExclusiveEvents = dataCompanyEvents?.totalExclusive || 0
  const countExclusiveCompanyInsights = dataCompanyInsights?.experiencesInfos.totalSchoolExperiences || 0

  const displayExclusivityMessage = !!customMessage
  const displayExclusivityLinks =
    countExclusiveJobAds > 0 || countExclusiveEvents > 0 || countExclusiveCompanyInsights > 0
  const displayExclusivity = intranet && (displayExclusivityMessage || displayExclusivityLinks)

  if (!displayExclusivity) return null

  return (
    <Layout hasBottomPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="company_exclusivity">
      <Layout.Column>
        <div
          className={clsx(styles.exclusivity, {
            [styles["exclusivity--2Cols"]]: displayExclusivityLinks && displayExclusivityMessage,
          })}
        >
          <div className={styles.exclusivity__message}>
            <div className={styles.exclusivity__title}>
              <Logo component={NextImage} src={logo || ""} width="40" height="40" alt="" />
              <Heading as="h3" variant="title3" weight="bold">
                {t("companies_client.information.exclusivity.title", { name, shortName })}
              </Heading>
            </div>
            {displayExclusivityMessage && (
              <RichText resetSpacing data-testid="company_exclusivity_custom_message" allowTargetAttribute>
                {customMessage}
              </RichText>
            )}
          </div>
          {displayExclusivityLinks && (
            <div
              className={clsx(styles.exclusivity__actions, {
                [styles["exclusivity__actions--row"]]: !displayExclusivityMessage,
              })}
              data-testid="company_exclusivity_links"
            >
              {countExclusiveJobAds > 0 && (
                <Link
                  component={NextLink}
                  href={`${companyPath}/job-offers?exclusive=reserved`}
                  className={styles.exclusivity__link}
                >
                  <Text variant="body1" weight="bold">
                    {t("companies_client.information.exclusivity.jobAds", { count: countExclusiveJobAds, shortName })}
                  </Text>
                  <Icon xlinkHref={`#${IconArrowRight.id}`} size={ICON_SIZES.BIG} />
                </Link>
              )}
              {countExclusiveEvents > 0 && (
                <Link
                  component="a"
                  href={`/${locale}/${EVENT_ROUTE}/search?query=${name}&exclusive=true`}
                  className={styles.exclusivity__link}
                  data-testid="company_exclusivity_link_events"
                >
                  <Text variant="body1" weight="bold">
                    {t("companies_client.information.exclusivity.events", { count: countExclusiveEvents, shortName })}
                  </Text>
                  <Icon xlinkHref={`#${IconArrowRight.id}`} size={ICON_SIZES.BIG} />
                </Link>
              )}
              {countExclusiveCompanyInsights > 0 && (
                <Link href={`/${locale}/${INSIGHT_ROUTE}?keywords=${name}`} className={styles.exclusivity__link}>
                  <Text variant="body1" weight="bold">
                    {t("companies_client.information.exclusivity.insights", {
                      count: countExclusiveCompanyInsights,
                      shortName,
                    })}
                  </Text>
                  <Icon xlinkHref={`#${IconArrowRight.id}`} size={ICON_SIZES.BIG} />
                </Link>
              )}
            </div>
          )}
        </div>
      </Layout.Column>
    </Layout>
  )
}
